// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import axios from "axios"
import { useNavigate } from "react-router-dom"

// Types
import type { SelectOptionType, SuccessFetch } from "types"
import type { ReviewType } from "../../types"

// Utils
import { useApi } from "utils"

// Types
type Data = ReviewType["data"]
interface AccType extends SelectOptionType {
  group_coa_id: number
}

export function useList(
  file_name?: string,
  file_status?: number
): {
  dataLocation: SelectOptionType[]
  dataType: AccType[]
  isLoading: boolean
  refetch: () => Promise<Data[]>
} {
  // Vars
  const defaultData: [] = []

  // Hooks
  const api = useApi()
  const navigate = useNavigate()
  const [isLoading, setLoading] = useState<boolean>(false)
  const [dataLocation, setDataLocation] = useState<SelectOptionType[]>(defaultData)
  const [dataType, setDataType] = useState<AccType[]>(defaultData)

  const refetch = (): Promise<Data[]> => {
    return new Promise<Data[]>((resolve) => {
      setLoading(true)

      axios.all([
        api.get(
          "/import_coa/viewfile",
          {
            params: {
              file_name,
              file_status
            }
          }
        ),
        api.get("/location/"),
        api.get("/acctype/"),
        api.get("/groupcoa/")
      ])
      .then(axios.spread((ress, locations, accs, groups) => {
        // Vars
        const res: SuccessFetch<{
          payload: ReviewType["fetch"][]
        }> = ress
        const location: SuccessFetch<{
          payload: {
            location_id: number
            location_name: string
          }[]
        }> = locations
        const acc: SuccessFetch<{
          payload: {
            account_type_id: number
            account_type_name: string
            group_coa_id: number
          }[]
        }> = accs
        const group: SuccessFetch<{
          payload: {
            group_coa_id: number
            group_coa_code: string
          }[]
        }> = groups
        const accType: AccType[] = acc.data.payload.map(item => ({
          group_coa_id: item.group_coa_id,
          label: item.account_type_name,
          value: item.account_type_id.toString()
        }))
        const locationList: SelectOptionType[] = location.data.payload.map(item => ({
          label: item.location_name,
          value: item.location_id.toString()
        }))

        setDataLocation(locationList)
        setDataType(accType)
        resolve(res.data.payload.map(item => {
          // Vars
          const coa_level = item.coa_level
          const group_coa_id: number = group.data.payload.find(i => i.group_coa_code === item.coa_code.split("-")[0])?.group_coa_id ?? 0

          // Functions
          // const getAccountType = (): string => {
          //   if (coa_level !== 1) {
          //     return accType.find(item => item.group_coa_id === group_coa_id)?.value ?? ""
          //   }

          //   return ""
          // }

          return {
            coa_level,
            group_coa_id,
            coa_cheque: item.coa_cheque ? "true" : "false",
            coa_code: item.coa_code,
            coa_name: item.coa_name,
            preview_id: item.preview_id,
            status: item.status,
            header_level: item.header_level.toString(),
            account_type_id: item.account_type_id?.toString() || "" , 
            location_id: item.location_id?.toString() ?? locationList.find(item => item.label === "Master")?.value ?? ""
          }
        }))
      })).catch(() => {
        resolve(defaultData)
      }).finally(() => {
        setLoading(false)
      })
    })
  }

  useEffect(() => {
    if (!file_name) {
      return navigate(
        "/setup/accounts/account-import",
        { replace: true }
      )
    }

    // eslint-disable-next-line
  }, [])

  return { dataLocation, dataType, isLoading, refetch }
}